/** Parse numeric charge from order API (supports common field names). */
export function parseChargeValue(order, keys, fallback = 0) {
  if (!order) return fallback;
  for (const key of keys) {
    const raw = order[key];
    if (raw === null || raw === undefined || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/** Normalize extra charge lines from API. */
export function parseExtraCharges(order) {
  const raw =
    order?.extra_charges ??
    order?.extraCharges ??
    order?.order_charges ??
    order?.additional_charges;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row, index) => ({
      id: row?.id ?? `extra-${index}`,
      label: String(row?.label ?? row?.name ?? row?.charge_name ?? "").trim(),
      amount: Number(row?.amount ?? row?.value ?? row?.charge_amount ?? 0),
    }))
    .filter((row) => row.label || Number.isFinite(row.amount));
}

/** Display value for open text charge inputs (keeps API decimals as typed). */
export function formatChargeFieldText(order, keys) {
  if (!order) return "";
  for (const key of keys) {
    const raw = order[key];
    if (raw === null || raw === undefined || raw === "") continue;
    return String(raw);
  }
  return "";
}

/** Parse open text input to number for PATCH body. */
export function parseNumericInput(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return 0;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : NaN;
}

/** Read charge breakdown from an order record. */
export function getOrderChargesFromOrder(order) {
  const totalShippingWeight = formatChargeFieldText(order, [
    "total_shipping_weight",
    "totalShippingWeight",
  ]);
  const shippingCostPerKg = formatChargeFieldText(order, [
    "shipping_cost_per_kg",
    "shippingCostPerKg",
  ]);
  const deliveryCharge = parseChargeValue(order, [
    "delivery_charge",
    "delivery_fee",
    "deliveryCharge",
    "deliveryFee",
  ]);
  const marketplaceFee = parseChargeValue(order, [
    "marketplace_fee",
    "marketplaceFee",
    "marketplace_charge",
  ]);
  const tax = parseChargeValue(order, ["tax", "estimated_tax", "tax_amount", "est_tax"]);
  const extraCharges = parseExtraCharges(order);
  return {
    totalShippingWeight,
    shippingCostPerKg,
    deliveryCharge,
    marketplaceFee,
    tax,
    extraCharges,
  };
}

/** delivery_charge = weight × rate (admin charges API). */
export function computeDeliveryFromShipping(weight, costPerKg) {
  const w = parseNumericInput(weight);
  const r = parseNumericInput(costPerKg);
  if (!Number.isFinite(w) || !Number.isFinite(r) || w <= 0 || r < 0) return null;
  const value = w * r;
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
}

/** Sum line-item subtotal from order_details. */
export function getItemsSubtotal(order) {
  const details = order?.order_details || [];
  return details
    .filter((d) => String(d?.item?.status ?? d?.status ?? "").toLowerCase() === "approved")
    .reduce((sum, d) => sum + Number(d.totalPrice || d.total_price || 0), 0);
}

/** Compute grand total from subtotal + charges. */
export function computeGrandTotal({
  deliveryCharge = 0,
  marketplaceFee = 0,
  tax = 0,
  extraCharges = [],
}) {
  const extras = (extraCharges || []).reduce(
    (sum, row) => sum + (Number.isFinite(Number(row.amount)) ? Number(row.amount) : 0),
    0
  );
  const base =
    Number(deliveryCharge) +
    Number(marketplaceFee) +
    Number(tax) +
    extras;
  return Number.isFinite(base) ? base : 0;
}

/** Payload for PATCH /v1/admin/order/:orderId/charges */
export function buildOrderChargesPayload({
  totalShippingWeight,
  shippingCostPerKg,
  deliveryCharge,
  marketplaceFee,
  tax,
  extraCharges,
  invoiceId,
}) {
  const weight = parseNumericInput(totalShippingWeight);
  const costPerKg = parseNumericInput(shippingCostPerKg);
  const grandTotal = computeGrandTotal({
    deliveryCharge,
    marketplaceFee,
    tax,
    extraCharges,
  });
  const trimmedInvoiceId = invoiceId != null ? String(invoiceId).trim() : "";
  return {
    total_shipping_weight: Number.isFinite(weight) && weight >= 0 ? weight : 0,
    shipping_cost_per_kg: Number.isFinite(costPerKg) && costPerKg >= 0 ? costPerKg : 0,
    delivery_charge: Number(deliveryCharge) || 0,
    marketplace_fee: Number(marketplaceFee) || 0,
    tax: Number(tax) || 0,
    extra_charges: (extraCharges || [])
      .filter((row) => String(row.label || "").trim())
      .map((row) => ({
        label: String(row.label).trim(),
        amount: Number(row.amount) || 0,
      })),
    grand_total: grandTotal,
    total_amount: grandTotal,
    invoice_id: trimmedInvoiceId,
  };
}

/** Prefer server grand total when present. */
export function resolveDisplayedGrandTotal(order, computed) {
  const fromApi = parseChargeValue(
    order,
    ["grand_total", "total_amount", "grandTotal", "totalAmount"],
    null
  );
  if (fromApi != null && Number.isFinite(fromApi) && fromApi > 0) return fromApi;
  return computed;
}

export function formatMoney(n, symbol = "₹") {
  const v = Number(n);
  if (!Number.isFinite(v)) return `${symbol}0.00`;
  return `${symbol}${v.toFixed(2)}`;
}

export function newExtraChargeRow() {
  return { id: `extra-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label: "", amount: 0 };
}
